'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { Globe, Mic, Smartphone } from 'lucide-react';

import { PLATFORM_COPY } from '@/lib/content';
import { cn } from '@/lib/utils';

type SurfaceName = (typeof PLATFORM_COPY.surfaces)[number];
type CycleName = (typeof PLATFORM_COPY.cycle)[number];

const CANVAS_W = 380;
const CANVAS_H = 602;

const ORBIT_PATH =
  'M 40 208 H 340 A 22 22 0 0 1 362 230 V 350 A 22 22 0 0 1 340 372 H 40 A 22 22 0 0 1 18 350 V 230 A 22 22 0 0 1 40 208';
const FEED_PATH = 'M 190 152 V 196';
const FEED_ARROW_PATH = 'M 190 152 V 200';

const RAYS = [
  { d: 'M 135 376 V 430 Q 135 440 118 440 H 118', duration: '2.6s', delay: '0s' },
  { d: 'M 155 376 V 490 Q 155 500 118 500 H 118', duration: '3.1s', delay: '1.3s' },
  { d: 'M 175 376 V 550 Q 175 560 118 560 H 118', duration: '2.2s', delay: '0.6s' },
  { d: 'M 245 376 V 430 Q 245 440 262 440 H 262', duration: '2.9s', delay: '2.1s' },
  { d: 'M 225 376 V 490 Q 225 500 262 500 H 262', duration: '2.4s', delay: '1.7s' },
  { d: 'M 205 376 V 550 Q 205 560 262 560 H 262', duration: '3.3s', delay: '0.9s' },
] as const;

const CYCLE_AT: Record<CycleName, { top: number; left: number }> = {
  IMPROVE: { top: 208, left: 56 },
  DEPLOY: { top: 208, left: 324 },
  CONVERSE: { top: 372, left: 324 },
  LEARN: { top: 372, left: 56 },
};

const CHATGPT_PATH =
  'M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9847 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z';

const CLAUDE_PATH =
  'm4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z';

const WHATSAPP_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

function StrokeGlyph({ children, color }: { children: ReactNode; color: string }) {
  return (
    <span className="inline-flex size-[18px] items-center justify-center" style={{ color }}>
      {children}
    </span>
  );
}

function BrandGlyph({ path, color }: { path: string; color: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-[18px]" fill={color}>
      <path d={path} />
    </svg>
  );
}

const SURFACE_MARK: Record<
  SurfaceName,
  { x: number; y: number; filled?: string; glyph: ReactNode }
> = {
  Claude: {
    x: 100,
    y: 440,
    glyph: <BrandGlyph path={CLAUDE_PATH} color="#D97757" />,
  },
  ChatGPT: {
    x: 280,
    y: 440,
    filled: '#10A37F',
    glyph: <BrandGlyph path={CHATGPT_PATH} color="#FFFFFF" />,
  },
  'Your website': {
    x: 100,
    y: 500,
    glyph: (
      <StrokeGlyph color="#2563EB">
        <Globe aria-hidden="true" className="size-[18px]" strokeWidth={1.8} />
      </StrokeGlyph>
    ),
  },
  'Branded App': {
    x: 280,
    y: 500,
    glyph: (
      <StrokeGlyph color="#7C3AED">
        <Smartphone aria-hidden="true" className="size-[18px]" strokeWidth={1.8} />
      </StrokeGlyph>
    ),
  },
  WhatsApp: {
    x: 100,
    y: 560,
    filled: '#25D366',
    glyph: <BrandGlyph path={WHATSAPP_PATH} color="#FFFFFF" />,
  },
  'Voice Agents': {
    x: 280,
    y: 560,
    glyph: (
      <StrokeGlyph color="#D97706">
        <Mic aria-hidden="true" className="size-[18px]" strokeWidth={1.8} />
      </StrokeGlyph>
    ),
  },
};

function Chip({ children, inverted = false }: { children: string; inverted?: boolean }) {
  return (
    <span
      className={cn(
        'rounded-[4px] border px-1.5 py-0.5 font-mono text-[11.5px] tracking-[0.08em] uppercase',
        inverted ? 'border-background/35 text-background' : 'border-foreground/35 text-foreground',
      )}
    >
      {children}
    </span>
  );
}

function SurfaceNode({
  label,
  x,
  y,
  filled,
  glyph,
}: {
  label: string;
  x: number;
  y: number;
  filled?: string;
  glyph: ReactNode;
}) {
  return (
    <div
      className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1"
      style={{ top: y, left: x }}
    >
      <span
        title={label}
        className={cn(
          'inline-flex size-9 items-center justify-center rounded-full',
          filled ? null : 'border border-border bg-background',
        )}
        style={filled ? { background: filled } : undefined}
      >
        {glyph}
      </span>
      <span className="font-mono text-[9.5px] tracking-widest whitespace-nowrap text-muted-foreground uppercase">
        {label}
      </span>
    </div>
  );
}

function DiagramCanvas() {
  return (
    <div className="relative" style={{ width: CANVAS_W, height: CANVAS_H }}>
      <svg
        width={CANVAS_W}
        height={CANVAS_H}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        aria-hidden="true"
        className="absolute inset-0"
      >
        <path d={FEED_PATH} stroke="var(--foreground)" strokeWidth="2" fill="none" />
        <polygon
          points="0,-4.5 9,0 0,4.5"
          fill="var(--foreground)"
          className="motion-reduce:hidden"
          style={{
            offsetPath: `path('${FEED_ARROW_PATH}')`,
            offsetRotate: 'auto',
            animation: 'packet 1.8s linear infinite',
          }}
        />
        <polygon
          points="0,-4.5 9,0 0,4.5"
          fill="var(--foreground)"
          className="motion-reduce:hidden"
          style={{
            offsetPath: `path('${ORBIT_PATH}')`,
            offsetRotate: 'auto',
            animation: 'orbit 7s linear infinite',
          }}
        />
        <polygon
          points="0,-4.5 9,0 0,4.5"
          fill="var(--foreground)"
          className="motion-reduce:hidden"
          style={{
            offsetPath: `path('${ORBIT_PATH}')`,
            offsetRotate: 'auto',
            animation: 'orbit 7s linear -3.5s infinite',
          }}
        />
        <rect
          x="18"
          y="208"
          width="344"
          height="164"
          rx="22"
          fill="none"
          stroke="var(--faint-foreground)"
          strokeWidth="1.5"
          strokeDasharray="2 6"
        />
        <rect x="32" y="220" width="316" height="140" rx="16" fill="var(--foreground)" />
        {RAYS.map(ray => (
          <path key={ray.d} d={ray.d} stroke="var(--border)" strokeWidth="1.5" fill="none" />
        ))}
        {RAYS.map(ray => (
          <circle
            key={ray.d}
            r="2.2"
            fill="var(--foreground)"
            className="motion-reduce:hidden"
            style={{
              offsetPath: `path('${ray.d}')`,
              animation: `packet ${ray.duration} ease-in-out ${ray.delay} infinite alternate`,
            }}
          />
        ))}
      </svg>

      <div className="absolute top-[148px] left-[190px] flex w-[280px] -translate-x-1/2 -translate-y-full flex-col items-center gap-1.5 rounded-[10px] border-[1.5px] border-brand bg-background px-3 py-3 text-center">
        <p className="font-mono text-base font-semibold tracking-label">{PLATFORM_COPY.brand}</p>
        <p className="font-mono text-[9px] leading-snug font-semibold tracking-[0.06em] text-muted-foreground uppercase">
          {PLATFORM_COPY.brandItems}
        </p>
      </div>

      <div className="absolute top-[290px] left-[190px] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-4 text-center">
        <p className="font-mono text-xl font-semibold tracking-label text-background">BAZAK</p>
        <div className="flex flex-col items-center gap-1">
          {PLATFORM_COPY.osChipRows.map(row => (
            <div key={row.join('-')} className="flex justify-center gap-1">
              {row.map(chip => (
                <Chip key={chip} inverted>
                  {chip}
                </Chip>
              ))}
            </div>
          ))}
        </div>
      </div>

      {PLATFORM_COPY.cycle.map(label => (
        <div
          key={label}
          className="absolute -translate-x-1/2 -translate-y-1/2 bg-card px-1.5 py-0.5 font-mono text-[11.5px] font-semibold tracking-label"
          style={CYCLE_AT[label]}
        >
          {label}
        </div>
      ))}

      {PLATFORM_COPY.surfaces.map(surface => (
        <SurfaceNode key={surface} label={surface} {...SURFACE_MARK[surface]} />
      ))}
    </div>
  );
}

const FIGURE_LABEL = `${PLATFORM_COPY.brand} feeds Bazak, which deploys the same agent to ${PLATFORM_COPY.surfaces.join(', ')}. The loop is ${PLATFORM_COPY.cycle.join(', ')}.`;

/** Brand → Bazak hub → every surface. Scales to the card box — cqw/cqh was dropping. */
export function ArchitectureDiagram({ className }: { className?: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const apply = () => {
      const { width, height } = frame.getBoundingClientRect();
      if (width < 1) return;
      const byWidth = width / CANVAS_W;
      const naturalHeight = width * (CANVAS_H / CANVAS_W);
      // Stretched lg column is taller than the portrait; contain so both edges meet the card.
      setScale(height > naturalHeight + 8 ? Math.min(byWidth, height / CANVAS_H) : byWidth);
    };
    const observer = new ResizeObserver(apply);
    observer.observe(frame);
    apply();
    return () => observer.disconnect();
  }, []);

  return (
    <figure
      dir="ltr"
      aria-label={FIGURE_LABEL}
      className={cn(
        'flex h-auto min-h-0 flex-col overflow-hidden rounded-xl border bg-card p-2 lg:h-full',
        className,
      )}
    >
      <div ref={frameRef} className="relative w-full max-lg:aspect-[380/602] lg:min-h-0 lg:flex-1">
        <div
          className="absolute top-0 left-0 origin-top-left"
          style={{
            width: CANVAS_W,
            height: CANVAS_H,
            transform: `scale(${scale})`,
          }}
        >
          <DiagramCanvas />
        </div>
      </div>
    </figure>
  );
}
